import { useMemo } from "react";
import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import { graphqlQuery } from "@/lib/api/graphql";
import { mockCustomers } from "@/lib/mock-customers";
import type { CustomerUI, CustomersKpisUI, CustomerCurrencyAggregationRow } from "@/types/ui/customers";
import type { RiskLevelCode } from "@/lib/risk-level";
import {
  type PaginationResult,
  type GraphQLConnection,
  pageToCursor,
  buildGraphQLSortVariable as buildSort,
  computeMockPagination,
  createDefaultPagination,
  computePaginationWithCount,
} from "@/lib/graphql-pagination";

export type { PaginationResult } from "@/lib/graphql-pagination";

// --- Params ---

export interface GetCustomersGraphQLParams {
  page?: number;
  pageSize?: number;
  search?: string;
  riskLevelCode?: string | null;
  currency?: string;
  paymentTerms?: string;
  collectionStrategyIds?: (number | null)[];
  creditControllerIds?: (number | null)[];
  accountManagerIds?: (number | null)[];
  userSortKey1?: string[];
  userSortKey2?: string[];
  userSortKey3?: string[];
  userSortKey4?: string[];
  onStop?: string;
  customerIds?: number[];
  outstandingMin?: number;
  outstandingMax?: number;
  overdueMin?: number;
  overdueMax?: number;
  creditLimitMin?: number;
  creditLimitMax?: number;
  dsoMin?: number;
  dsoMax?: number;
  outstandingNotZero?: boolean;
  customerStatus?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

// --- GraphQL types ---

interface CustomerGraphQLNode {
  customerId: number;
  customerName: string | null;
  accountNumber: string | null;
  outstanding: number;
  overdue: number;
  outstandingHome: number;
  overdueHome: number;
  outstandingCustomer: number;
  overdueCustomer: number;
  displayOutstanding: number;
  displayOverdue: number;
  creditLimit: number;
  currencyCreditLimit: number;
  dso: number | null;
  riskLevelCode: string | null;
  riskLevelName: string | null;
  lastPaymentDate: string | null;
  primaryContactEmail: string | null;
  primaryContactPhone: string | null;
  primaryContactFirstName: string | null;
  primaryContactSurname: string | null;
  address: string | null;
  primaryAddressLine: string | null;
  primaryAddressTown: string | null;
  primaryAddressCounty: string | null;
  primaryAddressPostCode: string | null;
  primaryAddressCountry: string | null;
  statementAddressLine: string | null;
  statementAddressTown: string | null;
  statementAddressCounty: string | null;
  statementAddressPostCode: string | null;
  statementAddressCountry: string | null;
  paymentTermsCode: string | null;
  collectionStrategyId: number | null;
  collectionStrategyName: string | null;
  creditControllerId: number | null;
  creditControllerName: string | null;
  creditControllerEmail: string | null;
  accountManagerId: number | null;
  accountManagerName: string | null;
  currencyCode: string | null;
  anyCurrency: boolean;
  transactionCurrencies: string | null;
  userSortKey1: string | null;
  userSortKey2: string | null;
  userSortKey3: string | null;
  userSortKey4: string | null;
  userSortKey1Label: string | null;
  userSortKey2Label: string | null;
  userSortKey3Label: string | null;
  userSortKey4Label: string | null;
  customerStatus: string | null;
  onStop: boolean;
}

interface CustomerListGraphQLResponse {
  customers: {
    customerList: GraphQLConnection<CustomerGraphQLNode>;
  };
}

interface CustomerCountGraphQLResponse {
  customers: {
    customerCount: number;
  };
}

interface CustomerTotalsGraphQLResponse {
  customers: {
    customerTotals: {
      totalCount: number;
      totalOutstanding: number;
      totalOverdue: number;
      highRiskCount: number;
    };
  };
}

interface CustomerCurrencyAggregationNode {
  currencyCode: string | null;
  outstandingHome: number;
  overdueHome: number;
  outstandingCustomer: number;
  overdueCustomer: number;
}

interface CustomerCurrencyAggregationGraphQLResponse {
  customers: {
    customerList: GraphQLConnection<CustomerCurrencyAggregationNode>;
  };
}

// --- GraphQL queries ---

const CUSTOMER_LIST_QUERY = `
  query CustomerList($first: Int, $after: String, $sort: [CustomerSortSortByInput!], $filter: CustomerFilterInput) {
    customers {
      customerList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            customerId
            customerName
            accountNumber
            outstanding
            overdue
            outstandingHome
            overdueHome
            outstandingCustomer
            overdueCustomer
            displayOutstanding
            displayOverdue
            creditLimit
            currencyCreditLimit
            dso
            riskLevelCode
            riskLevelName
            lastPaymentDate
            primaryContactEmail
            primaryContactPhone
            primaryContactFirstName
            primaryContactSurname
            address
            primaryAddressLine
            primaryAddressTown
            primaryAddressCounty
            primaryAddressPostCode
            primaryAddressCountry
            statementAddressLine
            statementAddressTown
            statementAddressCounty
            statementAddressPostCode
            statementAddressCountry
            paymentTermsCode
            collectionStrategyId
            collectionStrategyName
            creditControllerId
            creditControllerName
            creditControllerEmail
            accountManagerId
            accountManagerName
            currencyCode
            anyCurrency
            transactionCurrencies
            userSortKey1
            userSortKey2
            userSortKey3
            userSortKey4
            userSortKey1Label
            userSortKey2Label
            userSortKey3Label
            userSortKey4Label
            customerStatus
            onStop
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

const CUSTOMER_COUNT_QUERY = `
  query CustomerCount($filter: CustomerFilterInput) {
    customers {
      customerCount(filter: $filter)
    }
  }
`;

const CUSTOMER_TOTALS_QUERY = `
  query CustomerTotals($filter: CustomerFilterInput) {
    customers {
      customerTotals(filter: $filter) {
        totalCount
        totalOutstanding
        totalOverdue
        highRiskCount
      }
    }
  }
`;

const CUSTOMER_CURRENCY_AGGREGATION_QUERY = `
  query CustomerCurrencyAggregation($filter: CustomerFilterInput, $aggregate: CustomerSortGroupByInput) {
    customers {
      customerList(filter: $filter, aggregate: $aggregate) {
        edges {
          node {
            currencyCode
            outstandingHome
            overdueHome
            outstandingCustomer
            overdueCustomer
          }
        }
      }
    }
  }
`;

// --- Mapping ---

function mapGraphQLNodeToCustomerUI(node: CustomerGraphQLNode): CustomerUI {
  return {
    id: String(node.customerId),
    name: node.customerName || "",
    accountNumber: node.accountNumber || "",
    currency: (node.currencyCode as CustomerUI["currency"]) || "GBP",
    outstanding: node.outstanding,
    overdue: node.overdue,
    outstandingHome: node.outstandingHome,
    overdueHome: node.overdueHome,
    outstandingCustomer: node.outstandingCustomer,
    overdueCustomer: node.overdueCustomer,
    displayOutstanding: node.displayOutstanding,
    displayOverdue: node.displayOverdue,
    creditLimit: node.creditLimit,
    currencyCreditLimit: node.currencyCreditLimit,
    dso: node.dso ?? 0,
    risk: (node.riskLevelCode?.toLowerCase() as RiskLevelCode) || "none",
    riskLevelName: node.riskLevelName || undefined,
    lastPayment: node.lastPaymentDate || "",
    contact: node.primaryContactEmail || "",
    phone: node.primaryContactPhone || "",
    primaryContactFirstName: node.primaryContactFirstName || undefined,
    primaryContactSurname: node.primaryContactSurname || undefined,
    address: node.address || "",
    primaryAddressLine: node.primaryAddressLine || undefined,
    primaryAddressTown: node.primaryAddressTown || undefined,
    primaryAddressCounty: node.primaryAddressCounty || undefined,
    primaryAddressPostCode: node.primaryAddressPostCode || undefined,
    primaryAddressCountry: node.primaryAddressCountry || undefined,
    statementAddressLine: node.statementAddressLine || undefined,
    statementAddressTown: node.statementAddressTown || undefined,
    statementAddressCounty: node.statementAddressCounty || undefined,
    statementAddressPostCode: node.statementAddressPostCode || undefined,
    statementAddressCountry: node.statementAddressCountry || undefined,
    paymentTerms: (node.paymentTermsCode as CustomerUI["paymentTerms"]) || "Net 30",
    collectionStrategyId: node.collectionStrategyId ? String(node.collectionStrategyId) : "",
    collectionStrategyName: node.collectionStrategyName || undefined,
    creditControllerId: node.creditControllerId ? String(node.creditControllerId) : undefined,
    creditControllerName: node.creditControllerName || undefined,
    creditControllerEmail: node.creditControllerEmail || undefined,
    accountManagerId: node.accountManagerId ? String(node.accountManagerId) : undefined,
    accountManagerName: node.accountManagerName || undefined,
    isAnyCurrency: node.anyCurrency,
    transactionCurrencies: node.transactionCurrencies ? node.transactionCurrencies.split(",") : [],
    userSortKey1: node.userSortKey1 || undefined,
    userSortKey2: node.userSortKey2 || undefined,
    userSortKey3: node.userSortKey3 || undefined,
    userSortKey4: node.userSortKey4 || undefined,
    userSortKey1Label: node.userSortKey1Label || undefined,
    userSortKey2Label: node.userSortKey2Label || undefined,
    userSortKey3Label: node.userSortKey3Label || undefined,
    userSortKey4Label: node.userSortKey4Label || undefined,
    status: node.customerStatus || undefined,
    onStop: node.onStop,
  };
}

// --- Sort / Filter builders ---

export const sortFieldMap: Record<string, string> = {
  accountNumber: "AccountNumber",
  name: "CustomerName",
  outstanding: "OutstandingHome",
  overdue: "OverdueHome",
  creditLimit: "CreditLimit",
  dso: "Dso",
  risk: "RiskLevelCode",
  lastPayment: "LastPaymentDate",
  currency: "CurrencyCode",
  paymentTerms: "PaymentTermsCode",
  collectionStrategy: "CollectionStrategyName",
  creditController: "CreditControllerName",
  accountManager: "AccountManagerName",
  contact: "PrimaryContactEmail",
  phone: "PrimaryContactPhone",
  address: "Address",
  onStop: "OnStop",
};

function buildGraphQLSortVariable(sortBy?: string, sortOrder?: "asc" | "desc") {
  return buildSort(sortFieldMap, sortBy, sortOrder);
}

function buildNullableIdFilter(ids: (number | null)[]): Record<string, unknown> {
  const hasNull = ids.includes(null);
  const numericIds = ids.filter((v): v is number => v !== null);
  if (hasNull && numericIds.length > 0) {
    return { containsValues: [null, ...numericIds] };
  }
  if (hasNull) {
    return { isBlank: true };
  }
  if (numericIds.length === 1) {
    return { eq: numericIds[0] };
  }
  return { containsValues: numericIds };
}

export function buildGraphQLFilterVariable(params: GetCustomersGraphQLParams) {
  const filter: Record<string, unknown> = {};

  if (params.search) {
    filter.summary = params.search;
  }
  if (params.riskLevelCode !== undefined) {
    filter.riskLevelCode = params.riskLevelCode === null
      ? { isBlank: true }
      : { eq: params.riskLevelCode };
  }
  if (params.currency && params.currency !== "all") {
    filter.currencyCode = { eq: params.currency };
  }
  if (params.paymentTerms && params.paymentTerms !== "all") {
    filter.paymentTermsCode = { eq: params.paymentTerms };
  }
  if (params.collectionStrategyIds?.length) {
    filter.collectionStrategyId = buildNullableIdFilter(params.collectionStrategyIds);
  }
  if (params.creditControllerIds?.length) {
    filter.creditControllerId = buildNullableIdFilter(params.creditControllerIds);
  }
  if (params.accountManagerIds?.length) {
    filter.accountManagerId = buildNullableIdFilter(params.accountManagerIds);
  }
  if (params.userSortKey1 && params.userSortKey1.length > 0) {
    filter.userSortKey1 = { containsValues: params.userSortKey1 };
  }
  if (params.userSortKey2 && params.userSortKey2.length > 0) {
    filter.userSortKey2 = { containsValues: params.userSortKey2 };
  }
  if (params.userSortKey3 && params.userSortKey3.length > 0) {
    filter.userSortKey3 = { containsValues: params.userSortKey3 };
  }
  if (params.userSortKey4 && params.userSortKey4.length > 0) {
    filter.userSortKey4 = { containsValues: params.userSortKey4 };
  }
  if (params.customerIds && params.customerIds.length > 0) {
    filter.customerId = { containsValues: params.customerIds };
  }
  const hasCurrencyFilter = params.currency !== undefined && params.currency !== "all";
  if (params.outstandingNotZero) {
    const outstandingField = hasCurrencyFilter ? "outstandingCustomer" : "outstandingHome";
    filter[outstandingField] = { ne: 0 };
  } else if (params.outstandingMin !== undefined || params.outstandingMax !== undefined) {
    const outstandingFilter: Record<string, number> = {};
    if (params.outstandingMin !== undefined) outstandingFilter.ge = params.outstandingMin;
    if (params.outstandingMax !== undefined) outstandingFilter.le = params.outstandingMax;
    const outstandingField = hasCurrencyFilter ? "outstandingCustomer" : "outstandingHome";
    filter[outstandingField] = outstandingFilter;
  }
  if (params.overdueMin !== undefined || params.overdueMax !== undefined) {
    const overdueFilter: Record<string, number> = {};
    if (params.overdueMin !== undefined) overdueFilter.ge = params.overdueMin;
    if (params.overdueMax !== undefined) overdueFilter.le = params.overdueMax;
    const overdueField = hasCurrencyFilter ? "overdueCustomer" : "overdueHome";
    filter[overdueField] = overdueFilter;
  }
  if (params.creditLimitMin !== undefined || params.creditLimitMax !== undefined) {
    const creditLimitFilter: Record<string, number> = {};
    if (params.creditLimitMin !== undefined) creditLimitFilter.ge = params.creditLimitMin;
    if (params.creditLimitMax !== undefined) creditLimitFilter.le = params.creditLimitMax;
    const creditLimitField = hasCurrencyFilter ? "currencyCreditLimit" : "creditLimit";
    filter[creditLimitField] = creditLimitFilter;
  }
  if (params.dsoMin !== undefined || params.dsoMax !== undefined) {
    const dsoFilter: Record<string, number> = {};
    if (params.dsoMin !== undefined) dsoFilter.ge = params.dsoMin;
    if (params.dsoMax !== undefined) dsoFilter.le = params.dsoMax;
    filter.dso = dsoFilter;
  }
  if (params.customerStatus) {
    filter.customerStatus = { eq: params.customerStatus };
  }
  if (params.onStop && params.onStop !== "all") {
    filter.onStop = { eq: params.onStop === "true" };
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

// --- Mock helpers ---

function convertMockCustomersToUI(): CustomerUI[] {
  return mockCustomers.map((customer) => ({
    id: customer.id,
    name: customer.name,
    accountNumber: customer.accountNumber,
    currency: customer.currency,
    outstanding: customer.outstanding,
    overdue: customer.overdue,
    outstandingHome: customer.outstanding,
    overdueHome: customer.overdue,
    outstandingCustomer: customer.outstanding,
    overdueCustomer: customer.overdue,
    displayOutstanding: customer.outstanding,
    displayOverdue: customer.overdue,
    creditLimit: customer.creditLimit,
    currencyCreditLimit: customer.creditLimit,
    dso: customer.dso,
    risk: customer.risk,
    lastPayment: customer.lastPayment,
    contact: customer.contact,
    phone: customer.phone,
    address: customer.address,
    paymentTerms: customer.paymentTerms,
    collectionStrategyId: customer.collectionStrategyId,
    creditControllerId: customer.creditControllerId,
    accountManagerId: customer.accountManagerId,
    isAnyCurrency: customer.isAnyCurrency,
    transactionCurrencies: customer.transactionCurrencies,
    userSortKey1: customer.userSortKey1,
    userSortKey2: customer.userSortKey2,
    userSortKey3: customer.userSortKey3,
    userSortKey4: customer.userSortKey4,
    userSortKey1Label: customer.userSortKey1Label,
    userSortKey2Label: customer.userSortKey2Label,
    userSortKey3Label: customer.userSortKey3Label,
    userSortKey4Label: customer.userSortKey4Label,
    status: customer.status,
    onStop: customer.onStop ?? false,
  }));
}

function filterMockCustomers(customers: CustomerUI[], params: GetCustomersGraphQLParams): CustomerUI[] {
  return customers.filter((customer) => {
    const matchesSearch = !params.search ||
      customer.name.toLowerCase().includes(params.search.toLowerCase()) ||
      customer.accountNumber.toLowerCase().includes(params.search.toLowerCase());

    const matchesRisk = params.riskLevelCode === undefined ||
      (params.riskLevelCode === null ? !customer.risk || customer.risk === "none" : customer.risk === params.riskLevelCode);
    const matchesCurrency = !params.currency || params.currency === "all" || customer.currency === params.currency;
    const matchesPaymentTerms = !params.paymentTerms || params.paymentTerms === "all" || customer.paymentTerms === params.paymentTerms;
    const matchesStrategy = !params.collectionStrategyIds?.length ||
      params.collectionStrategyIds.some(id => id === null ? !customer.collectionStrategyId : customer.collectionStrategyId === String(id));
    const matchesController = !params.creditControllerIds?.length ||
      params.creditControllerIds.some(id => id === null ? !customer.creditControllerId : customer.creditControllerId === String(id));
    const matchesManager = !params.accountManagerIds?.length ||
      params.accountManagerIds.some(id => id === null ? !customer.accountManagerId : customer.accountManagerId === String(id));
    const matchesCustomerIds = !params.customerIds || params.customerIds.length === 0 ||
      params.customerIds.includes(Number(customer.id));

    const matchesSortKey1 = !params.userSortKey1 || params.userSortKey1.length === 0 ||
      (customer.userSortKey1 !== undefined && params.userSortKey1.includes(customer.userSortKey1));
    const matchesSortKey2 = !params.userSortKey2 || params.userSortKey2.length === 0 ||
      (customer.userSortKey2 !== undefined && params.userSortKey2.includes(customer.userSortKey2));
    const matchesSortKey3 = !params.userSortKey3 || params.userSortKey3.length === 0 ||
      (customer.userSortKey3 !== undefined && params.userSortKey3.includes(customer.userSortKey3));
    const matchesSortKey4 = !params.userSortKey4 || params.userSortKey4.length === 0 ||
      (customer.userSortKey4 !== undefined && params.userSortKey4.includes(customer.userSortKey4));

    const hasCurrencyFilter = params.currency !== undefined && params.currency !== "all";
    const outstandingValue = hasCurrencyFilter ? customer.outstandingCustomer : customer.outstandingHome;
    const overdueValue = hasCurrencyFilter ? customer.overdueCustomer : customer.overdueHome;
    const creditLimitValue = hasCurrencyFilter ? (customer.currencyCreditLimit ?? customer.creditLimit) : customer.creditLimit;
    const matchesOutstandingMin = params.outstandingMin === undefined || outstandingValue >= params.outstandingMin;
    const matchesOutstandingMax = params.outstandingMax === undefined || outstandingValue <= params.outstandingMax;
    const matchesOverdueMin = params.overdueMin === undefined || overdueValue >= params.overdueMin;
    const matchesOverdueMax = params.overdueMax === undefined || overdueValue <= params.overdueMax;
    const matchesCreditLimitMin = params.creditLimitMin === undefined || creditLimitValue >= params.creditLimitMin;
    const matchesCreditLimitMax = params.creditLimitMax === undefined || creditLimitValue <= params.creditLimitMax;
    const matchesDsoMin = params.dsoMin === undefined || customer.dso >= params.dsoMin;
    const matchesDsoMax = params.dsoMax === undefined || customer.dso <= params.dsoMax;
    const matchesOutstandingNotZero = !params.outstandingNotZero || customer.outstanding !== 0;
    const matchesStatus = !params.customerStatus || (customer.status ?? "Active") === params.customerStatus;
    const matchesOnStop = !params.onStop || params.onStop === "all" ||
      (params.onStop === "true" && customer.onStop === true) ||
      (params.onStop === "false" && (customer.onStop === false || customer.onStop === undefined));

    return matchesSearch && matchesRisk && matchesCurrency && matchesPaymentTerms &&
      matchesStrategy && matchesController && matchesManager && matchesCustomerIds &&
      matchesSortKey1 && matchesSortKey2 && matchesSortKey3 && matchesSortKey4 &&
      matchesOutstandingMin && matchesOutstandingMax &&
      matchesOverdueMin && matchesOverdueMax &&
      matchesCreditLimitMin && matchesCreditLimitMax &&
      matchesDsoMin && matchesDsoMax &&
      matchesOutstandingNotZero && matchesStatus && matchesOnStop;
  });
}

function computeMockCustomerPagination(items: CustomerUI[], page: number, pageSize: number): PaginationResult {
  return computeMockPagination(items.length, page, pageSize);
}

// --- Result types ---

export interface CustomersListResult {
  customers: CustomerUI[];
  pagination: PaginationResult;
}

// --- Hooks ---

function useCustomerCount(params?: GetCustomersGraphQLParams) {
  const apiConfig = useApiConfig();
  const filter = buildGraphQLFilterVariable(params ?? {});

  return useModeAwareQuery<number, Error>(
    ["customers-graphql-count", filter],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<CustomerCountGraphQLResponse>(
        apiConfig,
        CUSTOMER_COUNT_QUERY,
        variables,
        signal,
      );

      return response.customers.customerCount;
    },
    0,
    {
      staleTime: 30_000,
      refetchOnMount: "always",
    }
  );
}

export function useCustomersListGraphQL(params?: GetCustomersGraphQLParams) {
  const apiConfig = useApiConfig();
  const mockCustomersUI = convertMockCustomersToUI();
  const queryParams: GetCustomersGraphQLParams = { page: 1, pageSize: 20, sortBy: "name", sortOrder: "asc", ...params };
  const mockPage = queryParams.page ?? 1;
  const mockPageSize = queryParams.pageSize ?? 20;
  const filteredMock = filterMockCustomers(mockCustomersUI, queryParams);
  const mockPagination = computeMockCustomerPagination(filteredMock, mockPage, mockPageSize);
  const mockPagedCustomers = filteredMock.slice((mockPage - 1) * mockPageSize, mockPage * mockPageSize);
  const mockResult: CustomersListResult = { customers: mockPagedCustomers, pagination: mockPagination };

  return useModeAwareQuery<CustomersListResult, Error>(
    ["customers-graphql-list", queryParams],
    async ({ signal }) => {
      const page = queryParams.page ?? 1;
      const pageSize = queryParams.pageSize ?? 20;

      const variables: Record<string, unknown> = {
        first: pageSize,
        sort: buildGraphQLSortVariable(queryParams.sortBy, queryParams.sortOrder),
        filter: buildGraphQLFilterVariable(queryParams),
      };

      if (page > 1) {
        variables.after = pageToCursor(page);
      }

      const response = await graphqlQuery<CustomerListGraphQLResponse>(
        apiConfig,
        CUSTOMER_LIST_QUERY,
        variables,
        signal,
      );

      const connection = response.customers.customerList;
      const customers = connection.edges.map((x) => mapGraphQLNodeToCustomerUI(x.node));

      const pagination: PaginationResult = {
        page,
        pageSize,
        totalItems: customers.length,
        totalPages: 1,
        hasNextPage: connection.pageInfo.hasNextPage,
        hasPreviousPage: connection.pageInfo.hasPreviousPage,
      };

      return { customers, pagination };
    },
    mockResult,
    {
      staleTime: 0,
      refetchOnMount: "always",
    }
  );
}

// --- Totals hook ---

export function useCustomerTotals(params?: GetCustomersGraphQLParams) {
  const apiConfig = useApiConfig();
  const filter = buildGraphQLFilterVariable(params ?? {});

  const mockCustomersUI = convertMockCustomersToUI();
  const filteredMock = filterMockCustomers(mockCustomersUI, params ?? {});
  const mockTotals: CustomersKpisUI = {
    totalCustomers: filteredMock.length,
    totalOutstanding: filteredMock.reduce((sum, x) => sum + x.outstanding, 0),
    totalOverdue: filteredMock.reduce((sum, x) => sum + x.overdue, 0),
    highRiskCount: filteredMock.filter((x) => x.risk === "high" || x.risk === "critical").length,
  };

  return useModeAwareQuery<CustomersKpisUI, Error>(
    ["customers-graphql-totals", filter],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<CustomerTotalsGraphQLResponse>(
        apiConfig,
        CUSTOMER_TOTALS_QUERY,
        variables,
        signal,
      );

      const totals = response.customers.customerTotals;
      return {
        totalCustomers: totals.totalCount,
        totalOutstanding: totals.totalOutstanding,
        totalOverdue: totals.totalOverdue,
        highRiskCount: totals.highRiskCount,
      };
    },
    mockTotals,
    {
      staleTime: 30_000,
      refetchOnMount: "always",
    }
  );
}

// --- Currency aggregation hook ---

function useCustomerCurrencyAggregation(params?: GetCustomersGraphQLParams) {
  const apiConfig = useApiConfig();
  const filter = buildGraphQLFilterVariable(params ?? {});

  const mockRows = useMemo<CustomerCurrencyAggregationRow[]>(() => {
    const mockCustomersUI = convertMockCustomersToUI();
    const filteredMock = filterMockCustomers(mockCustomersUI, params ?? {});
    const currencyGroups = new Map<string, { outstandingHome: number; overdueHome: number; outstandingCustomer: number; overdueCustomer: number }>();
    for (const c of filteredMock) {
      const key = c.currency;
      const prev = currencyGroups.get(key) ?? { outstandingHome: 0, overdueHome: 0, outstandingCustomer: 0, overdueCustomer: 0 };
      currencyGroups.set(key, {
        outstandingHome: prev.outstandingHome + c.outstandingHome,
        overdueHome: prev.overdueHome + c.overdueHome,
        outstandingCustomer: prev.outstandingCustomer + c.outstandingCustomer,
        overdueCustomer: prev.overdueCustomer + c.overdueCustomer,
      });
    }
    return Array.from(currencyGroups.entries()).map(([currencyCode, vals]) => ({
      currencyCode,
      ...vals,
    }));
  }, [JSON.stringify(filter)]);

  return useModeAwareQuery<CustomerCurrencyAggregationRow[], Error>(
    ["customers-graphql-currency-aggregation", filter],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {
        aggregate: {
          field: "CurrencyCode",
          aggregates: [
            { type: "Sum", field: "OutstandingHome" },
            { type: "Sum", field: "OverdueHome" },
            { type: "Sum", field: "OutstandingCustomer" },
            { type: "Sum", field: "OverdueCustomer" },
          ],
        },
      };
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<CustomerCurrencyAggregationGraphQLResponse>(
        apiConfig,
        CUSTOMER_CURRENCY_AGGREGATION_QUERY,
        variables,
        signal,
      );

      return response.customers.customerList.edges.map((x) => ({
        currencyCode: x.node.currencyCode ?? "",
        outstandingHome: x.node.outstandingHome,
        overdueHome: x.node.overdueHome,
        outstandingCustomer: x.node.outstandingCustomer,
        overdueCustomer: x.node.overdueCustomer,
      }));
    },
    mockRows,
    {
      staleTime: 30_000,
      refetchOnMount: "always",
    }
  );
}

// --- Composite page data hook ---

const defaultPagination = createDefaultPagination(25);

export function useCustomersPageDataGraphQL(params?: GetCustomersGraphQLParams) {
  const listQuery = useCustomersListGraphQL(params);
  const countQuery = useCustomerCount(params);
  const totalsQuery = useCustomerTotals(params);
  const currencyAggregationQuery = useCustomerCurrencyAggregation(params);

  const basePagination = listQuery.data?.pagination ?? defaultPagination;
  const totalCount = countQuery.data ?? 0;
  const pagination = computePaginationWithCount(basePagination, totalCount, params?.pageSize);

  return {
    customers: listQuery.data?.customers ?? [],
    kpis: totalsQuery.data ?? {
      totalCustomers: 0,
      totalOutstanding: 0,
      totalOverdue: 0,
      highRiskCount: 0,
    },
    currencyAggregation: currencyAggregationQuery.data ?? [],
    pagination,
    isLoading: listQuery.isLoading || currencyAggregationQuery.isLoading,
    error: listQuery.error,
    refetch: () => {
      listQuery.refetch();
      countQuery.refetch();
      totalsQuery.refetch();
      currencyAggregationQuery.refetch();
    },
  };
}
